import {RepositoryPicker, type RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {Suspense, useId} from 'react'

import {TEST_IDS} from './constants/test-ids'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {TemplateListLoading} from './TemplateListLoading'
import {TemplateListPaneFromRepo} from './TemplateListPane'
import type {IssueCreationKind} from './utils/model'

export type RepositoryAndTemplatePickerDialogProps = {
  repository: RepositoryPickerRepository | undefined
  setRepository: (repository: RepositoryPickerRepository | undefined) => void
  topRepositories: RepositoryPickerRepository[]
  organization?: string
  onTemplateSelected: (filename: string, kind: IssueCreationKind) => void
}

export function RepositoryAndTemplatePickerDialog({
  repository,
  setRepository,
  topRepositories,
  organization,
  onTemplateSelected,
}: RepositoryAndTemplatePickerDialogProps) {
  const {optionConfig} = useIssueCreateConfigContext()
  const descriptionId = useId()
  const repoLeftMargin = optionConfig.insidePortal ? `ml-3` : `ml-0`

  return (
    <div className={optionConfig.insidePortal ? 'pt-2' : ''} data-testid={TEST_IDS.repositoryAndTemplateDialog}>
      <Field className={repoLeftMargin} name="Repository" />
      <div className={`${optionConfig.insidePortal ? `mb-2` : `mb-3`} ${repoLeftMargin}`}>
        <RepositoryPicker
          aria-describedby={descriptionId}
          initialRepository={repository}
          onSelect={setRepository}
          organization={organization}
          topRepositories={topRepositories}
          focusRepositoryPicker
          enforceAtleastOneSelected
          options={{hasIssuesEnabled: true, includeForks: true}}
        />
      </div>
      <Suspense fallback={<TemplateListLoading />}>
        {repository && (
          <TemplateListPaneFromRepo
            repositoryId={repository.id}
            onTemplateSelected={onTemplateSelected}
            descriptionId={descriptionId}
          />
        )}
      </Suspense>
    </div>
  )
}

function Field({name, className}: {name: string; className?: string}) {
  return (
    <div className={`pt-1 pb-1 ${className}`}>
      <span className={'text-bold'}>{name}</span>
      <span className={'ml-1 mb-1 fgColor-danger'}>*</span>
    </div>
  )
}

try{ RepositoryAndTemplatePickerDialog.displayName ||= 'RepositoryAndTemplatePickerDialog' } catch {}
try{ Field.displayName ||= 'Field' } catch {}